import { yupResolver } from '@hookform/resolvers/yup';
import { default as Switch } from 'antd/es/switch';
import type { Dispatch, FormEventHandler, SetStateAction } from 'react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import type { InferType } from 'yup';

import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';
import { hasValue } from 'utils/hasValue';

import { useGetCustomerAddress } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FormError } from '@atoms/FormError/FormError';

import { CancelButton } from '../ConfirmCancelDialog';
import {
  PREFILL_SENDER_SWITCH_TEST_ID,
  SENDER_SAME_AS_RECEIVER_SWITCHFIELD_TEST_ID,
  SENDER_STEP_NEXT_BUTTON
} from '../constants';
import { SpecificField } from '../fields/SpecificField';
import { setFormErrors } from '../formHelpers';
import type { StepProps } from '../steps';

type stepsDataProps = {
  setStepsData: Dispatch<
    SetStateAction<
      Partial<
        Record<
          | 'selectProduct'
          | 'payoutRequest'
          | 'sender'
          | 'receiver'
          | 'receiverBank'
          | 'summary'
          | 'intermediaryAgent'
          | 'intermediaryAgentAccount',
          any
        >
      >
    >
  >;
};

interface Props extends StepProps {
  sameReceiverInactive: boolean;
}

export const SenderStep = ({
  onSubmit,
  onCancel,
  fields,
  defaultValues,
  responseField,
  sameReceiverInactive,
  setStepsData
}: Props & stepsDataProps) => {
  const { isReceiverManagementEnabled } = useFeatureAccessControl();
  const organisationId = useOrganisationId<number>();

  // Validation
  const validationObject = useMemo(() => {
    if (hasValue(fields)) {
      const fieldsValidation = Object.keys(fields).reduce<
        Record<string, yup.BaseSchema>
      >((acc, fieldName: string) => {
        acc[fieldName] = yup
          .string()
          .label(fieldToDisplayableName(fieldName))
          .required();
        return acc;
      }, {});

      fieldsValidation['sameReceiver'] = yup
        .bool()
        .label('Is the Receiver the same person?')
        .required();

      return fieldsValidation;
    }
  }, [fields]);

  const schema = yup.object(validationObject).required();
  interface Inputs extends InferType<typeof schema> {
    base: null;
  }

  const form = useForm<Inputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { sameReceiver: false, ...defaultValues }
  });

  const onSubmitHandle: FormEventHandler<HTMLFormElement> = async (e) => {
    try {
      await form.handleSubmit(async (data) => {
        await onSubmit(data);
      })(e);
    } catch (error: any) {
      setFormErrors({
        errors: error.error[responseField],
        setError: form.setError,
        fields: Object.keys(schema.fields)
      });
    }
  };

  /* Related to prefill */
  const { data: { data: customerAddress } = {} } =
    useGetCustomerAddress(organisationId);

  const onSenderPrefill = (checked: boolean) => {
    let orgName = '';
    let orgIso = '';
    let orgAddress = '';
    let orgCity = '';
    let orgPostcode = '';
    let orgType = '';

    if (checked) {
      orgName = customerAddress?.organisation_name ?? '';
      orgIso = customerAddress?.country?.iso ?? '';
      orgAddress = customerAddress?.line1 ?? customerAddress?.line2 ?? '';
      orgCity = customerAddress?.city ?? '';
      orgPostcode =
        hasValue(customerAddress?.postal_code) &&
        customerAddress.postal_code !== ''
          ? customerAddress.postal_code
          : hasValue(customerAddress?.state) && customerAddress.state !== ''
            ? customerAddress.state
            : '';
      orgType = 'organization';
    }

    form.setValue('name', orgName);
    form.setValue('country_code', orgIso);
    form.setValue('address_lines', orgAddress);
    form.setValue('city', orgCity);
    form.setValue('postcode', orgPostcode);
    form.setValue('type', orgType);

    setStepsData((prev) => ({
      ...prev,
      sender: {
        name: orgName,
        country_code: orgIso,
        address_lines: orgAddress,
        city: orgCity,
        postcode: orgPostcode,
        type: orgType
      }
    }));
  };

  if (!fields) return null;

  return (
    <div className="flex justify-center pb-8">
      <form autoComplete="off" className="w-[90%]" onSubmit={onSubmitHandle}>
        {isReceiverManagementEnabled && (
          <div className="my-4 flex flex-row items-center justify-between">
            <p>Fill with organisation information?</p>
            <Switch
              checkedChildren="YES"
              unCheckedChildren="NO"
              onChange={onSenderPrefill}
              data-testid={PREFILL_SENDER_SWITCH_TEST_ID}
            />
          </div>
        )}

        {Object.keys(fields).map((fieldName) => {
          return (
            <SpecificField
              key={fieldName}
              fieldName={fieldName}
              fieldDefinition={fields[fieldName]}
              form={form}
              data-testid={`create-manual-payout-sender-${fieldName}`}
            />
          );
        })}

        <div className="mb-4 mt-6 flex items-center justify-between">
          <span>Is the Receiver the same person?</span>
          <Controller
            control={form.control}
            name="sameReceiver"
            render={({ field: { onChange } }) => (
              <Switch
                checkedChildren="YES"
                unCheckedChildren="NO"
                onChange={onChange}
                disabled={sameReceiverInactive}
                data-testid={SENDER_SAME_AS_RECEIVER_SWITCHFIELD_TEST_ID}
              />
            )}
          />
        </div>
        <div className="mt-10 flex gap-4">
          <CancelButton onAction={onCancel} />
          <PrimaryButton
            data-testid={SENDER_STEP_NEXT_BUTTON}
            size="large"
            htmlType="submit"
            className="w-full"
          >
            Next
          </PrimaryButton>
        </div>
        <FormError message={form.formState.errors.base?.message} />
      </form>
    </div>
  );
};
